/**
 * Projects facade (`/projects`).
 * Search-like list surface: filter / expand / fields / sort / limit / offset.
 */

import { Project } from '../../schemas/platform';
import { BackendClient, CatalogQuery, QueryEnvelope } from './BackendClient';

export class ProjectsClient extends BackendClient {
  constructor(apiKey?: string, webBackendUrl?: string) {
    super(apiKey, webBackendUrl);
    this.addEndpoint('projects', 'GET', 'required');
  }

  async queryProjects(query: CatalogQuery = {}): Promise<QueryEnvelope<Project>> {
    const raw = await this.getJson(
      'projects',
      this.catalogQuery({
        sort: query.sort ?? 'created_at:desc',
        limit: query.limit ?? 100,
        offset: query.offset ?? 0,
        ...query,
      }),
    );
    const envelope = this.asEnvelope<Record<string, any>>(raw);
    return {
      ...envelope,
      entities: envelope.entities.map((row) => new Project(row)),
    };
  }

  async getProject(
    projectId: string,
    query: Pick<CatalogQuery, 'expand' | 'fields'> = {},
  ): Promise<Project | null> {
    const route = `projects/${encodeURIComponent(projectId)}`;
    this.addEndpoint(route, 'GET', 'required');
    const raw = await this.getJson(route, this.catalogQuery(query));
    return raw ? new Project(raw) : null;
  }

  async upsertProject(
    displayName: string,
    description: string | null = null,
    projectId: string | null = null,
  ): Promise<string | null> {
    this.addEndpoint('projects', 'PUT', 'required');
    return this.putJson('projects', undefined, {
      display_name: displayName,
      description: description ?? undefined,
      project_id: projectId ?? undefined,
    });
  }

  async deleteProject(projectId: string): Promise<boolean> {
    const route = `projects/${encodeURIComponent(projectId)}`;
    this.addEndpoint(route, 'DELETE', 'required');
    const result = await this.deleteJson(route);
    return result === true || result == null;
  }

  async modifyMembers(
    projectId: string,
    members: { job_ids?: string[]; file_ids?: Array<string | number> } = {},
    add = true,
  ): Promise<boolean> {
    const route = `projects/${encodeURIComponent(projectId)}/members`;
    this.addEndpoint(route, 'POST', 'required');
    const result = await this.postJson(route, undefined, {
      add: add ? 'true' : 'false',
      job_ids: members.job_ids,
      file_ids: members.file_ids,
    });
    return result === true || result == null;
  }
}
